import {   useMemo } from 'react'
import { ComboBox } from './ComboBox'
import { IconComboBox  } from './IconComboBox'
import type {ButtonHTMLAttributes, ReactNode} from 'react';
import type {IconComboBoxOption} from './IconComboBox';
import type { PickerAction } from './PickerPanel'
import { cn } from '@/lib/utils'

export interface VariantComboBoxOption {
  value: string
  label: string
  icon: ReactNode
  keywords?: Array<string>
  meta?: ReactNode
  group?: string
  shortcut?: string
}

interface VariantComboBoxProps {
  options: Array<VariantComboBoxOption>
  selectedValue: string | undefined
  onChange: (value: string) => void
  variant?: 'default' | 'icon-only'
  placeholderText?: string
  searchText?: string
  buttonClassName?: string
  isDisabled?: boolean
  fontSize?: 'xs' | 'sm' | 'base'
  popoverOpen?: boolean
  onPopoverOpenChange?: (open: boolean) => void
  triggerProps?: ButtonHTMLAttributes<HTMLButtonElement> & {
    [key: `data-${string}`]: string | undefined
  }
  /** @deprecated Not rendered: the trigger's tooltip carries the shortcut (design 31a). */
  searchShortcutHint?: string | Array<string> | null
  enableNumericShortcuts?: boolean
  /** Render custom trigger icon for icon-only variant. Receives selected option. */
  renderTriggerIcon?: (option?: IconComboBoxOption) => ReactNode
  /** Fallback icon when no option is selected in icon-only variant */
  fallbackIcon?: ReactNode
  /** Empty state message when no options exist */
  emptyMessage?: ReactNode
  /** Trailing row under a rule in the picker, e.g. "Edit workflow…". */
  action?: PickerAction
}

/**
 * ComboBox wrapper that handles variant switching between full-text (ComboBox)
 * and icon-only (IconComboBox) modes. Used by PriorityComboBox, StatusComboBox,
 * and other pickers that need both display modes.
 */
export function VariantComboBox({
  options,
  selectedValue,
  onChange,
  variant = 'default',
  placeholderText = 'Select...',
  searchText = 'Search...',
  buttonClassName,
  isDisabled = false,
  fontSize = 'xs',
  popoverOpen,
  onPopoverOpenChange,
  triggerProps,
  enableNumericShortcuts = false,
  renderTriggerIcon,
  fallbackIcon,
  emptyMessage,
  action,
}: VariantComboBoxProps) {
  // Build options for ComboBox (text variant with inline icon + label)
  const textOptions = useMemo(
    () =>
      options.map((opt) => ({
        value: opt.value,
        labelText: opt.label,
        icon: opt.icon,
        meta: opt.meta,
        group: opt.group,
        shortcut: opt.shortcut,
        label: (
          <span className="flex items-center gap-2">
            {opt.icon}
            <span
              className={cn(
                fontSize === 'xs'
                  ? 'text-xs'
                  : fontSize === 'sm'
                    ? 'text-sm'
                    : 'text-base',
              )}
            >
              {opt.label}
            </span>
          </span>
        ),
      })),
    [options, fontSize],
  )

  // Build options for IconComboBox (icon variant)
  const iconOptions = useMemo<Array<IconComboBoxOption>>(
    () =>
      options.map((opt) => ({
        value: opt.value,
        label: opt.label,
        icon: opt.icon,
        keywords: opt.keywords ?? [opt.label.toLowerCase()],
        meta: opt.meta,
        group: opt.group,
        shortcut: opt.shortcut,
      })),
    [options],
  )

  if (emptyMessage && options.length === 0) {
    return <>{emptyMessage}</>
  }

  if (variant === 'icon-only') {
    return (
      <IconComboBox
        options={iconOptions}
        selectedValue={selectedValue}
        onChange={onChange}
        placeholderText={placeholderText}
        searchText={searchText}
        isDisabled={isDisabled}
        buttonClassName={buttonClassName}
        fontSize={fontSize}
        renderTriggerIcon={
          renderTriggerIcon ??
          ((option) => option?.icon ?? fallbackIcon)
        }
        buttonAriaLabel={placeholderText}
        isOpen={popoverOpen}
        onOpenChange={onPopoverOpenChange}
        triggerProps={triggerProps}
        enableNumericShortcuts={enableNumericShortcuts}
        action={action}
      />
    )
  }

  return (
    <ComboBox
      placeholderText={placeholderText}
      searchText={searchText}
      options={textOptions}
      defaultSelectedValue={selectedValue ?? ''}
      isDisabled={isDisabled}
      onChange={onChange}
      buttonClassName={buttonClassName}
      isOpen={popoverOpen}
      onOpenChange={onPopoverOpenChange}
      triggerProps={triggerProps}
      enableNumericShortcuts={enableNumericShortcuts}
      action={action}
    />
  )
}
